import Axios from 'axios'

export const DEFAULT_MAX_ATTEMPTS = 3
export const DEFAULT_BACKOFF_MS = 1000
// Parts in flight at once. Four is the AWS SDK's default queue size and what
// production uploaders generally settle on; more streams do not add throughput
// once the uplink is saturated. A note for whoever debugs a "network error on
// every part within a second" report: check the file is actually on disk first.
// A cloud-mount placeholder (macOS flag `dataless`, e.g. CloudMounter, iCloud)
// fails exactly like that in every browser and has nothing to do with this code.
export const DEFAULT_CONCURRENCY = 4

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Upload every part of an S3 multipart upload and resolve with the ETags in
 * part order, ready for the complete call.
 *
 * Each part is retried on its own with exponential backoff. The upload fails
 * only when one part has run out of attempts, at which point the remaining
 * parts are cancelled. Before ND-2388 a single failed request failed the
 * whole upload on the spot, so a 22 GB file never survived one dropped part.
 *
 * At most `concurrency` parts are in flight at once (see DEFAULT_CONCURRENCY
 * for why the browser is not left to queue them).
 *
 * @param {object} options
 * @param {Blob} options.file                    what the user picked
 * @param {number} options.chunkSize             part size from the create response
 * @param {Record<string, string>} options.preSignedUrls  part number (1-based, as a string) → URL
 * @param {(url: string, blob: Blob, config: object) => Promise<{headers: object}>} options.put
 *        performs one PUT; receives cancelToken and onUploadProgress in config
 * @param {(progress: {total: number, loaded: number}) => void} [options.onProgress]
 * @param {object} [options.cancelToken]         axios cancel token from the caller
 * @param {number} [options.maxAttempts]
 * @param {number} [options.backoffMs]           first retry delay; doubles each attempt
 * @param {number} [options.concurrency]         parts in flight at once
 * @param {(ms: number) => Promise<void>} [options.sleep]
 * @returns {Promise<Array<{ETag: string, PartNumber: number}>>}
 */
export async function uploadParts({
  file,
  chunkSize,
  preSignedUrls,
  put,
  onProgress,
  cancelToken,
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  backoffMs = DEFAULT_BACKOFF_MS,
  concurrency = DEFAULT_CONCURRENCY,
  sleep = defaultSleep
}) {
  const numParts = Math.ceil(file.size / chunkSize)
  const loadedByPart = new Array(numParts).fill(0)

  // One token shared by every part so that a part giving up, or the caller
  // cancelling, stops the parts that are still queued or in flight.
  const internal = Axios.CancelToken.source()
  if (cancelToken) cancelToken.promise.then((reason) => internal.cancel(reason?.message))

  const report = () => {
    if (!onProgress) return
    onProgress({ total: file.size, loaded: loadedByPart.reduce((sum, loaded) => sum + loaded, 0) })
  }

  const uploadPart = async (index) => {
    const url = preSignedUrls[String(index + 1)]
    const start = index * chunkSize
    const blob = file.slice(start, Math.min(start + chunkSize, file.size))

    for (let attempt = 1; ; attempt++) {
      // A retried part starts from zero, so its bytes replace the previous try
      // rather than adding to it; the total can dip but never double counts.
      loadedByPart[index] = 0

      try {
        const response = await put(url, blob, {
          cancelToken: internal.token,
          onUploadProgress: (progress) => {
            loadedByPart[index] = progress?.loaded ?? 0
            report()
          }
        })
        // Axios usually reports the final byte itself; only report here if it did not.
        if (loadedByPart[index] !== blob.size) {
          loadedByPart[index] = blob.size
          report()
        }

        return { ETag: parseETag(response?.headers?.etag), PartNumber: index + 1 }
      } catch (error) {
        if (Axios.isCancel(error)) throw error

        if (attempt >= maxAttempts) {
          internal.cancel(`part ${index + 1} failed after ${attempt} attempts`)
          throw withS3Message(error)
        }

        await sleep(backoffMs * 2 ** (attempt - 1))
      }
    }
  }

  // A fixed number of workers each take the next part; results land by index
  // so the order matches part numbers whatever order they finish in.
  const results = new Array(numParts)
  let next = 0
  const worker = async () => {
    while (next < numParts) {
      const index = next++
      results[index] = await uploadPart(index)
    }
  }
  await Promise.all(Array.from({ length: Math.min(concurrency, numParts) }, worker))

  return results
}

// S3 returns the ETag quoted; the complete call wants it bare.
function parseETag(etag) {
  if (typeof etag !== 'string') return etag
  try {
    return JSON.parse(etag)
  } catch {
    return etag
  }
}

// An S3 rejection is an XML body; lift its <Message> onto the error so the
// failure notification says "Request has expired" instead of "status code 403".
function withS3Message(error) {
  const body = error?.response?.data
  const match = typeof body === 'string' ? body.match(/<Message>([^<]*)<\/Message>/) : null
  if (match && error) {
    error.message = `${error.response.status ?? ''} ${match[1]}`.trim()
  }
  return error
}
